import React from 'react';
import { Tag, DollarSign, Briefcase } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';

const PROGRAM_LABELS = {
  school: 'School',
  women_entrepreneurship: 'Women Entrepreneurship',
  administration: 'Administration',
};

const F = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs">{label}</Label>
    {children}
  </div>
);

export const AssetFormDialog = ({ open, onOpenChange, editing, form, setForm, onSubmit, meta, categories }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="font-heading">{editing ? 'Edit Asset' : 'Register New Asset'}</DialogTitle>
        <DialogDescription>Capture full lifecycle information for this asset</DialogDescription>
      </DialogHeader>
      <form onSubmit={onSubmit} className="space-y-5">
        {/* Basic Info */}
        <div className="space-y-3 p-4 rounded-lg bg-muted/30">
          <div className="flex items-center gap-2"><Tag className="h-4 w-4 text-primary" /><h3 className="font-heading font-semibold text-sm">Basic Information</h3></div>
          <div className="grid grid-cols-2 gap-3">
            <F label="Asset Name *"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="asset-name-input" /></F>
            <F label="Asset Tag"><Input value={form.asset_tag} onChange={(e) => setForm({ ...form, asset_tag: e.target.value })} placeholder="Auto-generated if blank" /></F>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <F label="Category *">
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="asset-category-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {categories.map((c) => <SelectItem key={c} value={c}>{c.replace(/_/g, ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="QR Code / Barcode"><Input value={form.qr_code} onChange={(e) => setForm({ ...form, qr_code: e.target.value })} placeholder="Auto if blank" /></F>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <F label="Brand"><Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} /></F>
            <F label="Model #"><Input value={form.model_number} onChange={(e) => setForm({ ...form, model_number: e.target.value })} /></F>
            <F label="Serial #"><Input value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} /></F>
          </div>
          <F label="Vendor / Supplier"><Input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} /></F>
          <F label="Description"><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></F>
        </div>

        {/* Financial */}
        <div className="space-y-3 p-4 rounded-lg bg-green-50 dark:bg-green-900/10">
          <div className="flex items-center gap-2"><DollarSign className="h-4 w-4 text-green-600" /><h3 className="font-heading font-semibold text-sm">Financial Information</h3></div>
          <div className="grid grid-cols-3 gap-3">
            <F label="Purchase Date"><Input type="date" value={form.purchase_date} onChange={(e) => setForm({ ...form, purchase_date: e.target.value })} /></F>
            <F label="Purchase Cost"><Input type="number" value={form.purchase_cost} onChange={(e) => setForm({ ...form, purchase_cost: e.target.value })} /></F>
            <F label="Useful Life (yrs)"><Input type="number" value={form.useful_life_years} onChange={(e) => setForm({ ...form, useful_life_years: e.target.value })} /></F>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <F label="Funding Source"><Input value={form.funding_source} onChange={(e) => setForm({ ...form, funding_source: e.target.value })} placeholder="e.g., General Fund" /></F>
            <F label="Donor / Grant"><Input value={form.donor_grant} onChange={(e) => setForm({ ...form, donor_grant: e.target.value })} /></F>
            <F label="Warranty Expiry"><Input type="date" value={form.warranty_expiry} onChange={(e) => setForm({ ...form, warranty_expiry: e.target.value })} /></F>
          </div>
        </div>

        {/* Assignment */}
        <div className="space-y-3 p-4 rounded-lg bg-blue-50 dark:bg-blue-900/10">
          <div className="flex items-center gap-2"><Briefcase className="h-4 w-4 text-blue-600" /><h3 className="font-heading font-semibold text-sm">Assignment Information</h3></div>
          <div className="grid grid-cols-3 gap-3">
            <F label="Program">
              <Select value={form.program} onValueChange={(v) => setForm({ ...form, program: v })}>
                <SelectTrigger data-testid="asset-program-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{meta?.programs?.map((p) => <SelectItem key={p} value={p}>{PROGRAM_LABELS[p] || p}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Department"><Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} /></F>
            <F label="Location">
              <Select value={form.location} onValueChange={(v) => setForm({ ...form, location: v })}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{meta?.locations?.map((l) => <SelectItem key={l} value={l}>{l.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
              </Select>
            </F>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <F label="Assigned Staff"><Input value={form.assigned_to} onChange={(e) => setForm({ ...form, assigned_to: e.target.value })} placeholder="Staff name" /></F>
            <F label="Date Assigned"><Input type="date" value={form.date_assigned} onChange={(e) => setForm({ ...form, date_assigned: e.target.value })} /></F>
          </div>
        </div>

        {/* Status & Condition */}
        <div className="grid grid-cols-2 gap-3">
          <F label="Status *">
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="asset-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{meta?.statuses?.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="Condition">
            <Select value={form.condition} onValueChange={(v) => setForm({ ...form, condition: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{meta?.conditions?.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </F>
        </div>
        <F label="Notes"><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></F>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button type="submit" data-testid="asset-submit-btn">{editing ? 'Update' : 'Register'}</Button>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
);
